/**
 * Animated modal component.
 * @module src/Modal
 */

import React, {
    createContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    arrayOf,
    bool,
    element,
    func,
    object,
    string,
} from 'prop-types';
import {
    CSSTransition,
    TransitionGroup,
} from 'react-transition-group';

/**
 * React component that renders as its first child, or `null` if there are no
 * children in the `children` array.
 * @param {object} props - The component's props.
 * @param {React.ReactElement} props.children - The component's child.
 * @returns {React.ReactElement} The child.
 */
function FirstChild(props) {
    const { children } = props;
    return children[0] || null;
}

FirstChild.propTypes = {
    children: arrayOf(element).isRequired,
};

/**
 * Modal info, provided via `ModalContext`.
 * @typedef {object} ModalInfo
 * @property {boolean} isOpen - True if the modal is open.
 * @property {ModalOpen} open - Callback for opening the modal.
 * @property {ModalClose} close - Callback for closing the modal.
 */

/**
 * Modal open function.
 * @callback ModalOpen
 * @param {Event} event - The event.
 */

/**
 * Modal close function.
 * @callback ModalClose
 * @param {Event} event - The event.
 */

/**
 * Modal click handler.
 * @callback ModalClick
 * @param {Event} event - The event.
 * @param {ModalInfo} modalInfo - The modal info.
 */

/**
 * Context for modal state.
 */
export const ModalContext = createContext(null);

/**
 * Modal React component.
 * @param {object} props - The component's props.
 * @param {string?} props.className - Modal CSS class name.
 * @param {React.ReactElement} props.button - Modal-opening button.
 * @param {React.ReactElement} props.children - Modal contents.
 * @param {object?} props.transition - CSSTransition props.
 * @param {boolean?} props.noWindowHandler - If true, don't add a global window
 * handler for closing the modal.
 * @param {ModalClick} props.onClick - Modal click handler.
 * @returns {React.ReactElement} The component's elements.
 */
export default function Modal({
    className,
    button,
    children,
    transition,
    noWindowHandler,
    onClick,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const nodeRef = useRef(null);

    /**
     * Opens the modal.
     * @param {Event} [event] - If an event is passed, its propagation is
     * stopped.
     */
    function open(event) {
        event && event.stopPropagation();
        setIsOpen(true);
    }

    /**
     * Closes the modal.
     */
    function close() {
        setIsOpen(false);
    }

    useEffect(() => {
        if (noWindowHandler || !isOpen) {
            return;
        }

        window.addEventListener('click', close);
        return () => {
            window.removeEventListener('click', close);
        };
    }, [noWindowHandler, isOpen]);

    let modal = null;
    if (isOpen) {
        modal = children;

        if (transition) {
            modal = (
                <CSSTransition
                    nodeRef={nodeRef}
                    key={isOpen}
                    {...transition}
                >
                    {modal}
                </CSSTransition>
            );
        }
    }

    if (transition) {
        modal = (
            <TransitionGroup component={FirstChild}>
                {modal}
            </TransitionGroup>
        );
    }

    const modalInfo = { isOpen, open, close };
    let modalOnClick = null;
    if (onClick) {
        modalOnClick = function modalOnClick(event) {
            return onClick(event, modalInfo);
        };
    }

    return (
        <ModalContext value={modalInfo}>
            <span className={className} onClick={modalOnClick}>
                {button}
                {modal}
            </span>
        </ModalContext>
    );
}

Modal.propTypes = {
    className: string,
    button: element.isRequired,
    children: element.isRequired,
    transition: object,
    noWindowHandler: bool,
    onClick: func,
};
